/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from "@fullcalendar/interaction"
import { EventClickArg, EventSourceInput } from '@fullcalendar/core';
import ModalEntity, { Actions } from "app/modules/tp/components/Modal/ModalEntity";
import { emptyDriver, Driver } from 'app/modules/tp/drivers/_models';
import DriverFormBuilder from 'app/modules/tp/drivers/builders/DriverFormBuilder';
import DriverApi from 'app/modules/tp/drivers/_requests';
import { ToolbarWrapper } from '_metronic/layout/components/toolbar';
import { Ride } from 'app/modules/tp/rides/_models';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'app/modules/tp/rides/_constants';
import { IErrorResponse, IValidationErrorResponse, Response, } from "app/modules/tp/_apiResponse";
import { createErrorMessage, createSuccessMessage, createWarningMessage } from "app/partials/Alert";
import { ResourceAction } from "app/infrastructure/repository";
import ValidationError from "app/partials/form/ValidationError";
import Select from 'react-select';
import { status_colors, dynamicStatusColors } from 'app/modules/tp/drivers/_constants';

const DriversCalendarPage: FC = () => {
    const intl = useIntl();
    const [drivers, setDrivers] = React.useState<Driver[]>([]);

    const [filteredDrivers, setFilteredDrivers] = React.useState<number[]>([]);

    const [calendarKey, setCalendarKey] = React.useState<number>(0);
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [modalEntity, setModalEntity] = React.useState<Driver>(emptyDriver);
    const [modalError, setModalError] = React.useState<JSX.Element>(<></>);
    const [modalAlertMessage, setModalAlertMessage] = React.useState<JSX.Element>(<></>);
    const [alertMessage, setAlertMessage] = React.useState<JSX.Element>(<></>);
    const entityApi = DriverApi;

    const actionApi = async (action: ResourceAction, entity: Driver, callback?: ((e: Driver) => void)) => {
        let res: Response<Driver> | undefined;
        switch (action) {
            case "create":
                res = await entityApi.create(entity)
                break;
            case "update":
                res = await entityApi.update(entity.id, entity)
                break;
            case "delete":
                res = await entityApi.delete(entity.id)
                break;
            default:
                break;
        }
        if (!(res?.success)) {
            return res;
        }
        callback && res && callback(res?.data as Driver);
        return res;
    }

    const actionAndUpdateView = async (action: ResourceAction | "save", entity: Driver) => {
        if (action === "save") {
            action = (entity.id ? "update" : "create");
        }
        const res = await actionApi(action, entity)
        if (res?.success === false) {
            if ((res as IErrorResponse)?.type === "foreign_relation") {
                return setModalError(createWarningMessage("Oops!", "Non è possibile eliminare la risorsa perché è collegata ad altre entità"));
            }
            if ((res as IErrorResponse)?.type === "validation") {
                return setModalError(ValidationError({ error: (res as IValidationErrorResponse) }));
            }
        }
        if (res?.success) {
            setModalAlertMessage(createSuccessMessage("Operazione completata!", "L'operazione è stata completata con successo"));
            await fetchDrivers();
        } else {
            setModalError(createErrorMessage("Oops!", "Si è verificato un errore durante l'operazione"));
            //setAlertMessage(createErrorMessage("Oops!", "Si è verificato un errore durante l'operazione"));
        }
        return res;
    }

    const modalActions: Actions<Driver> = {
        save: {
            label: "Salva",
            onClick: async (entity: Driver) => {
                return (!!((await actionAndUpdateView("save", entity))?.success));
            }
        },
        destroy: {
            label: "Elimina",
            onClick: async (entity: Driver) => {
                if (!!((await actionAndUpdateView("delete", entity))?.success)) {
                    setAlertMessage(createSuccessMessage("Operazione completata!", "L'operazione è stata completata con successo"));
                    setShowModal(false);
                    setModalEntity(emptyDriver);
                    return true;
                }
                return false;
            }
        },
        cancel: {
            label: "Chiudi",
            onClick: async (entity: Driver) => {
                setShowModal(false);
                setModalEntity(emptyDriver);
                return true;
            }
        },
        duplicate: {
            label: "Duplica",
            onClick: async (entity: Driver) => {
                //TODO: implement
                return true;
            }
        },
        reload: {
            label: "Ricarica",
            onClick: async (entity: Driver) => {
                //TODO: implement
                return true;
            }
        }
    }

    const events: EventSourceInput = React.useMemo(() => {
        setCalendarKey(calendarKey + 1);
        const driverWithRides = drivers
            .filter((driver: Driver) => driver.rides?.length > 0)
            .flatMap((driver: Driver) => driver.rides
                ?.filter((ride: Ride) => (ride.start_departure_date && ride.end_departure_date))
                .map((ride: Ride) => {
                    const startDepartureDate = moment(ride.start_departure_date, DATE_TIME_FORMAT).toDate();
                    const endDepartureDate = moment(ride.end_departure_date, DATE_TIME_FORMAT).toDate();
                    return (
                        {
                            title: `${driver?.name ?? ""} ${driver?.surname ?? ""}`,
                            start: startDepartureDate,
                            end: endDepartureDate,
                            id: `dr_${(driver.id).toString()}`,
                            color: status_colors.OTHER,
                            driverId: driver.id
                        })
                })
            );
        const driverAbsent = drivers
            .filter((driver: Driver) => driver.driverDaysAbsences?.length > 0 || driver.driverHoursAbsences?.length > 0)
            .flatMap((driver: Driver) => [
                ...driver.driverDaysAbsences.map((absence) => ({
                    title: `${driver?.name ?? ""} ${driver?.surname ?? ""}`,
                    start: moment(absence.from, DATE_FORMAT).startOf('day').toDate(),
                    end: moment(absence.to, DATE_FORMAT).endOf('day').toDate(),
                    id: `dd_${(driver.id).toString()}`,
                    color: dynamicStatusColors(absence?.reason),
                    driverId: driver.id

                })),
                ...driver.driverHoursAbsences.map((absence) => ({
                    title: `${driver?.name ?? ""} ${driver?.surname ?? ""}`,
                    start: moment(`${absence.date} ${absence.from}`, DATE_TIME_FORMAT).toDate(),
                    end: moment(`${absence.date} ${absence.to}`, DATE_TIME_FORMAT).toDate(),
                    id: `dh_${(driver.id).toString()}`,
                    color: dynamicStatusColors(absence?.reason),
                }))
            ]);
        const totalDrivers = [...driverWithRides, ...driverAbsent];
        return totalDrivers.filter(driverEvent => {
            if (filteredDrivers.length > 0 && !filteredDrivers.find((driverID: number) => driverEvent?.driverId === driverID)) {
                return false;
            }
            return true;
        });
    }, [drivers, filteredDrivers]);

    const fetchDrivers = async () => setDrivers((await DriverApi.getAllNoPagination())?.data ?? []);

    React.useEffect(() => {
        fetchDrivers();
    }, []);

    const entityIcon = "fa-solid fa-clipboard-list";

    return (
        <>
            {
                showModal &&
                <ModalEntity<Driver>
                    key={modalEntity?.id}
                    title={{
                        icon: entityIcon,
                        label: `Scheda ${modalEntity?.name ?? ""} ${modalEntity?.surname ?? ""}`
                    }}
                    actions={modalActions}
                    eform={DriverFormBuilder()}
                    show={showModal}
                    initialEntity={modalEntity}
                    error={modalError}
                    message={modalAlertMessage}
                />
            }
            <ToolbarWrapper
                items={[
                    {
                        display: false,
                        component: {
                            type: 'button',
                            label: `Filtra`,
                            icon: 'fa-solid fa-plus',
                            //onClick: () => setShowCreateModal(true)
                        }
                    },
                    {
                        display: true,
                        component: {
                            type: 'custom',
                            key: "driversFilter",
                            render: () =>
                                <>
                                    <label className="form-label">Filtra per mezzo</label>
                                    <Select
                                        key="driversFilter"
                                        closeMenuOnSelect={false}
                                        isMulti
                                        options={drivers?.map((driver: Driver) => ({
                                            key: driver?.id,
                                            value: driver?.id,
                                            label: `${driver?.name ?? ""} ${driver?.surname ?? ""}`
                                        }))}
                                        onChange={(e) => {
                                            setFilteredDrivers(e?.map((e: { key: number }) => e?.key) ?? []);
                                        }}
                                    />
                                </>
                        },
                    },
                ]}
            />
            {
                alertMessage
            }
            <div className={'app-container'}>
                {/* begin::Row */}
                <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
                    {/* begin::Col */}
                    <div className='col-xxl-12'>
                        <div className="card card-custom card-flush">
                            <div className="card-body">
                                <FullCalendar
                                    key={calendarKey}
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                    locale={intl.locale}
                                    headerToolbar={{
                                        left: "prev,next,today",
                                        center: "title",
                                        right: ""
                                    }}
                                    firstDay={1}
                                    initialView="dayGridMonth"
                                    events={events}
                                    eventClick={async (info: EventClickArg) => {
                                        const entityID = parseInt(info.event.id.split("_")[1]);
                                        const actionEntity = (await DriverApi.getFull(entityID, {}))?.data ?? emptyDriver;
                                        setModalEntity(actionEntity);
                                        setShowModal(true);
                                    }}

                                />
                            </div>
                        </div>

                    </div>
                    {/* end::Col */}
                </div>
                {/* end::Row */}
            </div>
        </>
    );
};

const DriversCalendarWrapper: FC = () => {
    return (
        <>
            <DriversCalendarPage />
        </>
    )
};

export { DriversCalendarWrapper }
